import { useState, useMemo, useEffect } from "react";
import { format, subDays } from "date-fns";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  useToast,
  useDisclosure,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "./Orders.css";
import { selectOrders } from "../../../store/selectors/OrderSlice";
import {
  getStatusColor,
  getStatusTextColor,
} from "../../../helpers/GetStatusColors";
import { Order, OrderState } from "../../../entity/Order";
import { WSOrder } from "../../../services/remote/order/WSOrder";
import { setOrders } from "../../../store/slices/OrderSlice";
import { AddNewOrder } from "./AddNewOrder";
import { Badge } from "../../components/Badge";
import { COLORS } from "../../../utils/Colors";
import { selectListDeliveryCompanies } from "../../../store/selectors/DeliverySelector";
import { getDeliveryCompanyImage } from "../../../helpers/GetConnectedDeliveryCompanies";
import { FaBarcode } from "react-icons/fa6";
import {
  FiSearch,
  FiX,
  FiDownload,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiPhone,
  FiShoppingBag,
  FiClock,
  FiCheckCircle,
  FiTrendingUp,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiInbox,
} from "react-icons/fi";
import { ExportManager } from "../../../helpers/exportManager/ExportManager";
import { CONSTANTS } from "../../../utils/Constants";

const DATE_FORMAT = "yyyy-MM-dd";

const STATUSES = [
  "all",
  "pending",
  "completed",
  "delivered",
  "canceled",
  "returned",
] as const;

const DATE_PRESETS = [
  { key: "today", days: 1, labelKey: "orders.today", fallback: "Today" },
  { key: "7d", days: 7, labelKey: "orders.last_7_days", fallback: "7 days" },
  {
    key: "30d",
    days: 30,
    labelKey: "orders.last_30_days",
    fallback: "30 days",
  },
];

const parseLocalDate = (value: string, endOfDay = false) =>
  new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00"}`);

export const OrdersScreen = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const todayStr = format(new Date(), DATE_FORMAT);

  const [dateRange, setDateRange] = useState({
    startDate: format(subDays(new Date(), 6), DATE_FORMAT),
    endDate: todayStr,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const dispatch = useDispatch();
  const instance = WSOrder.getInstance();

  const orders = useSelector(selectOrders);
  const deliveryCompanies = useSelector(selectListDeliveryCompanies);

  /* ---------- helpers ---------- */

  const getTotalAmount = (order: Order): number => {
    if (order.status !== OrderState.PENDING) return order.totalAmount ?? 0;

    const itemsTotal =
      order.cart?.reduce((sum, item) => {
        if (item?.selectedPack) {
          return sum + (item.selectedPack.discount ?? item.selectedPack.price);
        }
        return sum + item.price * item.quantity;
      }, 0) ?? 0;

    return itemsTotal + (order.deliveryPrice || 0);
  };

  const activePreset = DATE_PRESETS.find(
    (p) =>
      dateRange.endDate === todayStr &&
      dateRange.startDate ===
        format(subDays(new Date(), p.days - 1), DATE_FORMAT),
  )?.key;

  const applyPreset = (days: number) => {
    setDateRange({
      startDate: format(subDays(new Date(), days - 1), DATE_FORMAT),
      endDate: todayStr,
    });
    setCurrentPage(1);
  };

  /* ---------- derived data ---------- */

  // Orders matching date + search (used for status chip counts)
  const baseOrders = useMemo(() => {
    const start = parseLocalDate(dateRange.startDate).getTime();
    const end = parseLocalDate(dateRange.endDate, true).getTime();
    const term = searchTerm.trim().toLowerCase();

    return (orders ?? []).filter((order) => {
      if (!order.createdAt) return false;
      const time = new Date(order.createdAt).getTime();
      const matchesDate = time >= start && time <= end;
      const matchesSearch =
        term === "" ||
        (order.id && order.id.toLowerCase().includes(term)) ||
        (order.phone && order.phone.includes(searchTerm.trim()));
      return matchesDate && matchesSearch;
    });
  }, [orders, dateRange, searchTerm]);

  const filteredOrders = useMemo(
    () =>
      statusFilter === "all"
        ? baseOrders
        : baseOrders.filter((order) => order.status === statusFilter),
    [baseOrders, statusFilter],
  );

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: baseOrders.length };
    baseOrders.forEach((o) => {
      counts[o.status] = (counts[o.status] ?? 0) + 1;
    });
    return counts;
  }, [baseOrders]);

  const totalSum = useMemo(
    () =>
      filteredOrders.reduce((sum, order) => sum + (order.totalAmount || 0), 0),
    [filteredOrders],
  );

  const stats = useMemo(
    () => ({
      total: baseOrders.length,
      pending: statusCounts["pending"] ?? 0,
      done: (statusCounts["completed"] ?? 0) + (statusCounts["delivered"] ?? 0),
    }),
    [baseOrders, statusCounts],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOrders.length / itemsPerPage),
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);

  // Keep the page valid when filters shrink the result set
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageNumbers = useMemo(() => {
    const visible = Math.min(5, totalPages);
    let start = Math.max(1, currentPage - 2);
    if (start + visible - 1 > totalPages) start = totalPages - visible + 1;
    return Array.from({ length: visible }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  const hasActiveFilters = searchTerm !== "" || statusFilter !== "all";

  /* ---------- handlers ---------- */

  const paginate = (pageNumber: number) =>
    setCurrentPage(Math.min(Math.max(1, pageNumber), totalPages));

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleCreateOrder = async (order: Order) => {
    try {
      const response = await instance.createOrder(order);
      dispatch(setOrders([{ ...order, id: response }, ...orders]));
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateOrder = async (updatedOrder: Order) => {
    try {
      await instance.updateOrder(updatedOrder);
      const updatedOrders = orders.map((order) =>
        order.id === updatedOrder.id
          ? { ...updatedOrder, createdAt: order.createdAt }
          : order,
      );
      dispatch(setOrders(updatedOrders));
      onClose();
    } catch (error) {
      console.error("Error updating order:", error);
      toast({
        title: t("toast.error"),
        description: t("toast.an_error_occurred"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    if (
      window.confirm(t("orders.are_you_sure_you_want_to_delete_this_order"))
    ) {
      try {
        await instance.deleteOrder(orderId);
        dispatch(setOrders(orders.filter((order) => order.id !== orderId)));
      } catch (error) {
        console.error("Error deleting order:", error);
      }
    }
  };

  const handleAddClick = () => {
    setSelectedOrder(null);
    onOpen();
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setCurrentPage(1);
  };

  const getInitial = (name?: string) =>
    (name?.trim()?.[0] ?? "?").toUpperCase();

  /* ---------- render ---------- */

  return (
    <div className="order-container">
      {/* Header */}
      <div className="order-header">
        <div className="order-heading">
          <h2 className="order-title">{t("sidebar.orders", "Orders")}</h2>
          <span className="order-count">{orders?.length ?? 0}</span>
        </div>

        <div className="order-header-actions">
          <button
            className="order-secondary-button"
            onClick={() =>
              ExportManager.exportCSV(
                filteredOrders,
                CONSTANTS.EXPORTED_ORDER_CSV_FILE_NAME,
              )
            }
          >
            <FiDownload size={15} />
            {t("buttons.export_csv")}
          </button>

          <button
            className="order-secondary-button"
            onClick={() =>
              ExportManager.exportPDF(
                filteredOrders.map((order) => ({
                  customer: order.name,
                  phone: order.phone,
                  address: order.address,
                  status: order.status,
                  totalAmount: order.totalAmount,
                  createdAt: order.createdAt?.toLocaleDateString() || "",
                })),
                CONSTANTS.EXPORTED_ORDER_PDF_FILE_NAME,
              )
            }
          >
            <FiDownload size={15} />
            {t("buttons.export_pdf")}
          </button>

          <button className="order-add-button" onClick={handleAddClick}>
            <FiPlus size={16} />
            {t("orders.add_order")}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="order-stats">
        <div className="order-stat-card">
          <div className="order-stat-icon order-stat-icon-neutral">
            <FiShoppingBag size={18} />
          </div>
          <div className="order-stat-body">
            <span className="order-stat-label">
              {t("orders.total_orders", "Total orders")}
            </span>
            <span className="order-stat-value">{stats.total}</span>
          </div>
        </div>

        <div className="order-stat-card">
          <div className="order-stat-icon order-stat-icon-warning">
            <FiClock size={18} />
          </div>
          <div className="order-stat-body">
            <span className="order-stat-label">{t("orders.pending")}</span>
            <span className="order-stat-value">{stats.pending}</span>
          </div>
        </div>

        <div className="order-stat-card">
          <div className="order-stat-icon order-stat-icon-success">
            <FiCheckCircle size={18} />
          </div>
          <div className="order-stat-body">
            <span className="order-stat-label">
              {t("orders.fulfilled", "Completed / Delivered")}
            </span>
            <span className="order-stat-value">{stats.done}</span>
          </div>
        </div>

        <div className="order-stat-card">
          <div className="order-stat-icon order-stat-icon-brand">
            <FiTrendingUp size={18} />
          </div>
          <div className="order-stat-body">
            <span className="order-stat-label">{t("orders.total")}</span>
            <span className="order-stat-value">
              {totalSum.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              <small> TND</small>
            </span>
          </div>
        </div>
      </div>

      {/* Table card */}
      <div className="order-table-card">
        {/* Filters */}
        <div className="order-toolbar">
          <div className="order-field order-field-search">
            <label className="order-field-label">
              {t("orders.search_by_id_or_phone")}
            </label>
            <div className="order-search">
              <FiSearch size={16} className="order-search-icon" />
              <input
                type="text"
                className="order-input order-search-input"
                placeholder={t("orders.search_by_id_or_phone")}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
              {searchTerm && (
                <button
                  className="order-search-clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <FiX size={14} />
                </button>
              )}
            </div>
          </div>

          <div className="order-field">
            <label className="order-field-label">{t("orders.from_date")}</label>
            <input
              type="date"
              className="order-input"
              value={dateRange.startDate}
              max={dateRange.endDate}
              onChange={(e) => {
                setDateRange((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                }));
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="order-field">
            <label className="order-field-label">{t("orders.to_date")}</label>
            <input
              type="date"
              className="order-input"
              value={dateRange.endDate}
              min={dateRange.startDate}
              max={todayStr}
              onChange={(e) => {
                setDateRange((prev) => ({ ...prev, endDate: e.target.value }));
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="order-presets">
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.key}
                className={`order-preset ${
                  activePreset === preset.key ? "order-preset-active" : ""
                }`}
                onClick={() => applyPreset(preset.days)}
              >
                {t(preset.labelKey, preset.fallback)}
              </button>
            ))}
          </div>
        </div>

        {/* Status chips */}
        <div className="order-chips" role="tablist">
          {STATUSES.map((status) => (
            <button
              key={status}
              role="tab"
              aria-selected={statusFilter === status}
              className={`order-chip ${
                statusFilter === status ? "order-chip-active" : ""
              }`}
              onClick={() => {
                setStatusFilter(status);
                setCurrentPage(1);
              }}
            >
              {status === "all"
                ? t("orders.all_statuses")
                : t(`orders.${status}`)}
              <span className="order-chip-count">
                {statusCounts[status] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        {filteredOrders.length === 0 ? (
          <div className="order-empty">
            <div className="order-empty-icon">
              <FiInbox size={28} />
            </div>
            <h3>{t("orders.no_orders_found")}</h3>
            <p>
              {hasActiveFilters
                ? t(
                    "orders.try_adjusting",
                    "Try adjusting your search or filters.",
                  )
                : t(
                    "orders.empty_range",
                    "No orders in the selected date range.",
                  )}
            </p>
            {hasActiveFilters && (
              <button className="order-secondary-button" onClick={resetFilters}>
                {t("orders.reset_filters", "Reset filters")}
              </button>
            )}
          </div>
        ) : (
          <div className="order-table-wrap">
            <Table variant="unstyled" className="order-table">
              <Thead>
                <Tr>
                  <Th>{t("orders.customer")}</Th>
                  <Th>{t("orders.phone")}</Th>
                  <Th>{t("orders.total_amount")}</Th>
                  <Th>{t("orders.status")}</Th>
                  <Th>{t("orders.date")}</Th>
                  <Th></Th>
                  <Th>{t("orders.actions")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {currentOrders.map((order) => (
                  <Tr key={order.id} className="order-row">
                    <Td
                      className="order-customer-cell"
                      data-label={t("orders.customer")}
                    >
                      <div className="order-customer">
                        <div className="order-avatar">
                          {getInitial(order.name)}
                        </div>
                        <div className="order-customer-info">
                          <span className="order-customer-name">
                            {order.name || "N/A"}
                          </span>
                          {order.id && (
                            <span className="order-customer-id">
                              #{order.id.slice(-6).toUpperCase()}
                            </span>
                          )}
                        </div>
                      </div>
                    </Td>

                    <Td data-label={t("orders.phone")}>
                      {order.phone ? (
                        <span className="order-phone">
                          <FiPhone size={13} />
                          {order.phone}
                        </span>
                      ) : (
                        "N/A"
                      )}
                    </Td>

                    <Td data-label={t("orders.total_amount")}>
                      <span className="order-amount">
                        {getTotalAmount(order)} <small>TND</small>
                      </span>
                    </Td>

                    <Td data-label={t("orders.status")}>
                      <div className="order-status-group">
                        <Badge
                          size="md"
                          text={t(`orderStatus.${order.status}`)}
                          color={getStatusColor(order.status).toString()}
                          textColor={getStatusTextColor(
                            order.status,
                          ).toString()}
                        />
                        {order.deliveryReference && (
                          <Badge
                            text={order.deliveryReference}
                            color={COLORS.Background.Primary}
                            textColor={COLORS.Text.Primary}
                          >
                            <FaBarcode />
                          </Badge>
                        )}
                      </div>
                    </Td>

                    <Td data-label={t("orders.date")}>
                      {order.createdAt ? (
                        <div className="order-date">
                          <span className="order-date-day">
                            {format(order.createdAt, "PP")}
                          </span>
                          <span className="order-date-time">
                            {format(order.createdAt, "p")}
                          </span>
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </Td>

                    <Td className="order-delivery-cell" data-label="">
                      {order.deliveryCompany && (
                        <Image
                          src={getDeliveryCompanyImage(
                            deliveryCompanies!!,
                            order.deliveryCompany,
                          )}
                          alt="Delivery Company"
                          className="order-delivery-logo"
                        />
                      )}
                    </Td>

                    <Td className="order-actions" data-label="">
                      <button
                        className="order-action-button"
                        aria-label="Edit order"
                        title="Edit"
                        onClick={() => {
                          setSelectedOrder(order);
                          onOpen();
                        }}
                      >
                        <FiEdit2 size={16} />
                      </button>
                      <button
                        className="order-action-button order-action-danger"
                        aria-label="Delete order"
                        title="Delete"
                        onClick={() => handleDeleteOrder(order.id!!)}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </div>
        )}

        {/* Footer: pagination */}
        {filteredOrders.length > 0 && (
          <div className="order-footer">
            <div className="order-footer-info order-hide-mobile">
              {t("orders.showing", "Showing")} {indexOfFirstItem + 1}–
              {Math.min(indexOfLastItem, filteredOrders.length)}{" "}
              {t("orders.of")} {filteredOrders.length}
            </div>

            <div className="order-footer-controls">
              <div className="order-per-page">
                <span className="order-hide-mobile">
                  {t("orders.items_per_page")}:
                </span>
                <select
                  className="order-select order-select-sm"
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="order-pagination">
                <button
                  className="order-page-button"
                  onClick={() => paginate(1)}
                  disabled={currentPage === 1}
                  aria-label="First page"
                >
                  <FiChevronsLeft size={15} />
                </button>
                <button
                  className="order-page-button"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <FiChevronLeft size={15} />
                </button>

                {pageNumbers.map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`order-page-button ${
                      currentPage === pageNum ? "order-page-active" : ""
                    }`}
                    onClick={() => paginate(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}

                <button
                  className="order-page-button"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <FiChevronRight size={15} />
                </button>
                <button
                  className="order-page-button"
                  onClick={() => paginate(totalPages)}
                  disabled={currentPage === totalPages}
                  aria-label="Last page"
                >
                  <FiChevronsRight size={15} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <AddNewOrder
        isOpen={isOpen}
        onClose={() => {
          onClose();
          setSelectedOrder(null);
        }}
        onCreate={handleCreateOrder}
        onUpdate={handleUpdateOrder}
        order={selectedOrder!!}
      />
    </div>
  );
};
